import React, { useState } from 'react';
import { ACCOUNT_SEARCH_ENDPOINT, ACCOUNT_ASSIGNMENT_ENDPOINT } from '../../config/api';
import Navbar from '../layout/Navbar';
import { useNavigate } from 'react-router-dom';
import { decryptData } from '../../utils/encrypt';

function AccountSearch() {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  const getUserId = () => {
    try {
      const enc = localStorage.getItem('suyog_user');
      if (!enc) return 0;
      const dec = decryptData(enc);
      return dec?.user_id || 0;
    } catch (e) {
      console.error('Token decrypt failed:', e);
      return 0;
    }
  };

  const handleAddNew = () => {
    navigate('/account/new');
  };

  const handleRefreshAccess = async () => {
    const user_id = getUserId();
    if (!user_id) {
      alert('Invalid user session. Please login again.');
      return;
    }
    try {
      setRefreshing(true);
      const res = await fetch(ACCOUNT_ASSIGNMENT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id }),
      });
      const data = await res.json();
      if (res.ok && data?.success) {
        alert('Account access refreshed successfully.');
        // Optionally re-run the current search to reflect access changes:
        if (searchTerm.trim()) await handleSearch();
      } else {
        alert(data?.message || 'Failed to refresh access');
      }
    } catch (err) {
      console.error('Refresh error:', err);
      alert('Server error during refresh');
    } finally {
      setRefreshing(false);
    }
  };

  const handleSearch = async () => {
    try {
      const user_id = getUserId();
      if (!user_id) {
        alert('User not authenticated');
        return;
      }
      const res = await fetch(
        `${ACCOUNT_SEARCH_ENDPOINT}?query=${encodeURIComponent(searchTerm)}&user_id=${user_id}`
      );
      const data = await res.json();
      if (res.ok) setResults(data);
      else alert(data.error || 'Failed to fetch accounts');
    } catch (err) {
      console.error('Search error:', err);
      alert('Server error');
    }
  };

  const maskAccountName = (name) =>
    `${name[0]} x x x ${name[name.length - 1]}`;

  return (
    <>
      <Navbar />
      <div className="container mt-5">
        <div className="card shadow-lg border-0 rounded-4">
          <div
            className="card-header text-white rounded-top-4 d-flex justify-content-between align-items-center"
            style={{
              background: 'linear-gradient(90deg, #007bff, #0056b3)',
              padding: '1rem 1.5rem',
            }}
          >
            <h4 className="mb-0">Search Accounts</h4>

            <div className="d-flex gap-2">
              {/* 🔁 Refresh Access (before Add New Account) */}
              <button
                className="btn btn-outline-light"
                onClick={handleRefreshAccess}
                disabled={refreshing}
                title="Rebuild account access for users"
              >
                {refreshing ? 'Refreshing…' : '🔁 Refresh Access'}
              </button>

              {/* + Add New Account */}
              <button
                className="btn btn-light text-primary fw-bold"
                onClick={handleAddNew}
              >
                + Add New Account
              </button>
            </div>
          </div>

          <div className="card-body p-4">
            <div className="input-group mb-4">
              <input
                type="text"
                className="form-control"
                placeholder="Search by name or city"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button className="btn btn-primary" onClick={handleSearch}>
                Search
              </button>
            </div>

            <div className="row">
              {results.length === 0 && (
                <div className="text-muted">No results found.</div>
              )}

              {results.map((acc) => (
                <div className="col-md-6 mb-4" key={acc.id}>
                  <div className="card shadow-sm rounded-4 h-100">
                    <div className="card-body d-flex justify-content-between align-items-start flex-column">
                      <div className="w-100">
                        <h5 className="card-title mb-2">
                          {acc.has_access ? acc.account_name : maskAccountName(acc.account_name)}
                        </h5>
                        <p className="mb-2 text-muted">{acc.city}</p>

                        <p className="mb-1 small text-secondary">
                          Created By: {acc.created_by_name}
                          {acc.created_on ? ` | ${acc.created_on}` : ''}
                        </p>

                        {acc.updated_by_name && (
                          <p className="mb-1 small text-secondary">
                            Updated By: {acc.updated_by_name}
                            {acc.updated_on ? ` | ${acc.updated_on}` : ''}
                          </p>
                        )}
                      </div>

                      <div className="mt-3 w-100 d-flex justify-content-between align-items-center">
                        <span className={`badge ${acc.has_access ? 'bg-success' : 'bg-secondary'}`}>
                          {acc.has_access ? 'Has Access' : 'No Access'}
                        </span>

                        {acc.has_access && (
                          <div>
                            <button
                              className="btn btn-outline-primary btn-sm me-2"
                              onClick={() =>
                                navigate(`/account/view/${acc.id}/${acc.account_token}`)
                              }
                            >
                              View
                            </button>
                            <button
                              className="btn btn-outline-warning btn-sm"
                              onClick={() =>
                                navigate(`/account/edit/${acc.id}/${acc.account_token}`)
                              }
                            >
                              Edit
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AccountSearch;
